/**
 * StatusBadge - Shared status rendering component
 *
 * Used across timesheets, leave, and other modules for consistent
 * status color coding. Single source of truth for status → color mapping.
 */

import { TEXT } from './Typography';
interface StatusBadgeProps {
  status: string;
  label?: string;
  variant?: 'default' | 'dot';
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

/** Canonical status → colour mapping (shared across all modules) */
export const STATUS_COLORS: Record<string, string> = {
  // Core workflow statuses
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  submitted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending_approval: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'pending approval': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  partial: 'bg-orange-100 text-orange-800 border-orange-200',
  'partially approved': 'bg-orange-100 text-orange-800 border-orange-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  posted: 'bg-blue-100 text-blue-800 border-blue-200',
  posting: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',

  // General statuses
  active: 'bg-green-100 text-green-800 border-green-200',
  inactive: 'bg-gray-100 text-gray-800 border-gray-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200',
  'in-progress': 'bg-yellow-100 text-yellow-800 border-yellow-200',

  // Content lifecycle (Learning module). Published is deliberately green rather
  // than the blue used for 'posted': a published course is live to learners, and
  // that is the state an author is looking for at a glance.
  published: 'bg-green-100 text-green-800 border-green-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200',

  // Enrolment lifecycle (Learning module). 'passed' and 'failed' are graded
  // outcomes and must not be confused with approval states above.
  not_started: 'bg-gray-100 text-gray-800 border-gray-200',
  passed: 'bg-green-100 text-green-800 border-green-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  pending_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  expired: 'bg-red-100 text-red-800 border-red-200',
  expiring: 'bg-amber-100 text-amber-800 border-amber-200',

  // Training requirements (training/requirementStatus.ts maps a requirement's
  // status onto these). Booked is a secondary status beside the primary one.
  compliant: 'bg-green-100 text-green-800 border-green-200',
  not_yet_due: 'bg-sky-100 text-sky-800 border-sky-200',
  unassigned: 'bg-violet-100 text-violet-800 border-violet-200',
  not_applicable: 'bg-gray-100 text-gray-600 border-gray-200',
  booked: 'bg-blue-100 text-blue-800 border-blue-200',

  // Publication window (homepage announcements)
  live: 'bg-green-100 text-green-800 border-green-200',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',

  // Roster swing confirmation
  awaiting_response: 'bg-amber-100 text-amber-800 border-amber-200',
  accepted: 'bg-green-100 text-green-800 border-green-200',
  declined: 'bg-red-100 text-red-800 border-red-200',
  dismissed: 'bg-gray-100 text-gray-600 border-gray-200',
};

/** Human-readable labels for statuses */
export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending: 'Pending',
  pending_approval: 'Pending Approval',
  'pending approval': 'Pending Approval',
  partial: 'Partial',
  'partially approved': 'Partially Approved',
  approved: 'Approved',
  rejected: 'Rejected',
  posted: 'Posted',
  posting: 'Posting...',
  cancelled: 'Cancelled',
  active: 'Active',
  inactive: 'Inactive',
  completed: 'Completed',
  'in-progress': 'In Progress',

  // Learning module
  published: 'Published',
  archived: 'Archived',
  not_started: 'Not Started',
  passed: 'Passed',
  failed: 'Failed',
  pending_review: 'Awaiting Review',
  expired: 'Expired',
  expiring: 'Expiring Soon',
  compliant: 'Compliant',
  not_yet_due: 'Not Yet Due',
  unassigned: 'Unassigned',
  not_applicable: 'Not Applicable',
  booked: 'Booked',
  live: 'Live',
  scheduled: 'Scheduled',

  // Roster swing confirmation
  awaiting_response: 'Awaiting response',
  accepted: 'Accepted',
  declined: 'Declined',
  dismissed: 'Dismissed',
};

const DOT_COLORS: Record<string, string> = {
  draft: 'bg-gray-400',
  submitted: 'bg-yellow-400',
  pending: 'bg-yellow-400',
  pending_approval: 'bg-yellow-400',
  'pending approval': 'bg-yellow-400',
  partial: 'bg-orange-400',
  'partially approved': 'bg-orange-400',
  approved: 'bg-green-400',
  rejected: 'bg-red-400',
  posted: 'bg-blue-400',
  posting: 'bg-blue-400',
  cancelled: 'bg-gray-400',
  active: 'bg-green-400',
  inactive: 'bg-gray-400',
  completed: 'bg-blue-400',
  'in-progress': 'bg-yellow-400',

  // Learning module
  published: 'bg-green-400',
  archived: 'bg-gray-400',
  not_started: 'bg-gray-400',
  passed: 'bg-green-400',
  failed: 'bg-red-400',
  pending_review: 'bg-yellow-400',
  expired: 'bg-red-400',
  expiring: 'bg-amber-400',
  compliant: 'bg-green-400',
  not_yet_due: 'bg-sky-400',
  unassigned: 'bg-violet-400',
  not_applicable: 'bg-gray-400',
  booked: 'bg-blue-400',
  live: 'bg-green-400',
  scheduled: 'bg-blue-400',

  // Roster swing confirmation
  awaiting_response: 'bg-amber-400',
  accepted: 'bg-green-400',
  declined: 'bg-red-400',
  dismissed: 'bg-gray-400',
};

const SIZE_CLASSES: Record<string, string> = {
  xs: 'px-2.5 py-0.5 text-xs',
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-1 text-sm',
};

export default function StatusBadge({
  status,
  label,
  variant = 'default',
  size = 'xs',
  className = '',
}: StatusBadgeProps) {
  const normalizedStatus = status?.toLowerCase() || 'draft';
  const colorClass = STATUS_COLORS[normalizedStatus] || STATUS_COLORS.draft;
  const displayLabel = label || STATUS_LABELS[normalizedStatus] || status;

  if (variant === 'dot') {
    const dotColorClass = DOT_COLORS[normalizedStatus] || DOT_COLORS.draft;
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <span className={`h-2 w-2 rounded-full ${dotColorClass}`} />
        <span className={TEXT.label}>{displayLabel}</span>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center ${SIZE_CLASSES[size]} font-medium rounded-full border ${colorClass} ${className}`}
    >
      {displayLabel}
    </span>
  );
}
